import type { DocumentSnapshot, ViewDescriptor } from '@/features/types';
import type { LintReport } from '@jsonfmt/core';

import { capabilitiesFor, type EditorCapabilities } from './capabilities';

/**
 * THE EDITOR TAB — ADR-0020, docs/ARCHITECTURE.md §8.
 *
 * v1 registers exactly one descriptor, and the tab strip therefore renders nothing. The file
 * exists in the shape a second tab will need, and it is the whole of what a second tab has to
 * copy: a pure `select`, a lazy `load`, a label KEY (never a literal), and an icon.
 *
 * ══ THIS MODULE MUST NOT IMPORT `@codemirror/*`, DIRECTLY OR TRANSITIVELY ══
 *
 * `features/registry.ts` imports it EAGERLY — that is what a registry is. Everything reachable
 * from here is in the initial chunk. The editor engine is roughly 400 KiB before compression,
 * so a stray `import { EditorView } from '@codemirror/view'` here would put it in the first
 * paint and make `load()` a lazy import of something already loaded.
 *
 * That is the reason `capabilitiesFor` lives in its own CodeMirror-free module: the descriptor
 * reports what the editor will run, and the extension array that actually runs it is built
 * behind the dynamic import. `view.test.ts` runs in bare Node, which is the mechanical proof
 * that nothing here reaches the engine.
 */

export interface EditorTabView {
  readonly text: string;
  readonly version: number;
  readonly bytes: number;
  readonly capabilities: EditorCapabilities;
  /** `false` ⇒ the panel renders the blocked explanation instead of mounting CodeMirror. */
  readonly editorEnabled: boolean;
  /** `true` ⇒ the soft-limit banner is shown (2–10 MiB). */
  readonly degraded: boolean;
  readonly isEmpty: boolean;
  readonly report: LintReport | null;
  /**
   * `reportVersion < version`: the report describes an older text (ADR-0009). The editor keeps
   * showing the underlines it has — CodeMirror maps them through the edits — but anything that
   * states a VERDICT must show a neutral state rather than an out-of-date one.
   */
  readonly reportStale: boolean;
  readonly linting: boolean;
}

/**
 * One-slot memo keyed on snapshot IDENTITY.
 *
 * `select` is called on every render of the view host, and `DocumentSnapshot` is a frozen
 * object replaced only when something changes, so identity is exactly the right key — no deep
 * comparison, no `version` arithmetic, and no risk of a stale hit. It is a module-level
 * variable rather than a `WeakMap` because there is one document on screen; holding the last
 * one costs a pointer and never grows.
 */
let memo: { readonly doc: DocumentSnapshot; readonly view: EditorTabView } | null = null;

/** PURE, synchronous, React-free. Tested in bare Node (`view.test.ts`). */
export function selectEditorView(doc: DocumentSnapshot): EditorTabView {
  if (memo !== null && memo.doc === doc) return memo.view;

  const capabilities = capabilitiesFor(doc.size);
  const view: EditorTabView = {
    text: doc.text,
    version: doc.version,
    bytes: doc.bytes,
    capabilities,
    editorEnabled: capabilities.mounted,
    degraded: doc.size === 'large',
    isEmpty: doc.text.length === 0,
    report: doc.report,
    reportStale: doc.report !== null && doc.reportVersion < doc.version,
    linting: doc.linting,
  };

  memo = { doc, view };
  return view;
}

/** Drops the memo. Exported for tests; nothing in the application calls it. */
export function resetEditorViewMemo(): void {
  memo = null;
}

export const editorView: ViewDescriptor<EditorTabView> = {
  id: 'editor',
  labelKey: 'view.editor.label',
  icon: 'editor',
  select: selectEditorView,
  // The ONE dynamic import in the feature. Everything CodeMirror is behind it.
  load: () => import('./EditorPanel'),
};

export default editorView;
